import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Edit, Trash2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ConversationQuestionDialog } from "./ConversationQuestionDialog";

interface ConversationQuestion {
  id: string;
  question_text: string;
  question_type_id: string;
  respondent_type: "subject" | "manager" | "both";
  is_required: boolean;
  order_index: number;
  stage_id: string;
  options: any;
  question_types: {
    name: string;
  };
  conversation_stages: {
    name: string;
    conversation_templates: {
      name: string;
    };
  };
}

export const ConversationQuestionsManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<ConversationQuestion | null>(null);

  const { data: questions, isLoading, refetch } = useQuery({
    queryKey: ['admin-conversation-questions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('conversation_questions')
        .select(`
          id,
          question_text,
          question_type_id,
          respondent_type,
          is_required,
          order_index,
          stage_id,
          options,
          question_types(name),
          conversation_stages(
            name,
            conversation_templates(name)
          )
        `)
        .order('order_index');

      if (error) throw error;
      return data as ConversationQuestion[];
    },
  });

  const filteredQuestions = questions?.filter(question =>
    question.question_text.toLowerCase().includes(searchTerm.toLowerCase()) ||
    question.conversation_stages.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    question.conversation_stages.conversation_templates.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (question: ConversationQuestion) => {
    setSelectedQuestion(question);
    setIsDialogOpen(true);
  };

  const handleAdd = () => {
    setSelectedQuestion(null);
    setIsDialogOpen(true);
  };

  const handleDelete = async (questionId: string) => {
    if (!confirm("Are you sure you want to delete this question?")) return;

    try {
      const { error } = await supabase
        .from('conversation_questions')
        .delete()
        .eq('id', questionId);

      if (error) throw error;
      refetch();
    } catch (error) {
      console.error('Error deleting question:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search conversation questions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Button onClick={handleAdd}>
          <Plus className="h-4 w-4 mr-2" />
          Add Question
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Questions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{questions?.length || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Required Questions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {questions?.filter(q => q.is_required).length || 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Subject & Manager</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {questions?.filter(q => q.respondent_type === 'both').length || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Conversation Questions ({filteredQuestions?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Question</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Respondent</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>Template</TableHead>
                <TableHead>Required</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredQuestions?.map((question) => (
                <TableRow key={question.id}>
                  <TableCell className="font-medium">{question.order_index}</TableCell>
                  <TableCell className="max-w-md">
                    <div className="truncate" title={question.question_text}>
                      {question.question_text}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{question.question_types?.name}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{question.respondent_type}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{question.conversation_stages.name}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{question.conversation_stages.conversation_templates.name}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={question.is_required ? "default" : "secondary"}>
                      {question.is_required ? "Yes" : "No"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(question)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(question.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ConversationQuestionDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        question={selectedQuestion}
        onSuccess={() => {
          refetch();
          setIsDialogOpen(false);
        }}
      />
    </div>
  );
};